import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import {
  Box, Typography, TextField, Select, MenuItem, FormControl,
  Button, IconButton, Chip, InputAdornment, Dialog, CircularProgress, Tooltip
} from "@mui/material";
import {
  Close, ArrowBack, Undo, Redo, CheckCircle,
  ContentCopy, Search, Add, Tag, PlayArrow,
  OpenInFull, DeleteOutline, InfoOutlined,
} from "@mui/icons-material";
import Buttons from "../Controls/Buttons/Button";
import CustomizedSnackbars from "../Snackbars/CustomizedSnackbars";
import "./formulaBuilder.scss";

const TOKEN_COLORS = {
  paren: { bg: "#E2E8F0", color: "#1E293B", border: "#94A3B8" },
  number: { bg: "#FEF3C7", color: "#713F12", border: "#F59E0B" },
  operator: { bg: "#E2E8F0", color: "#1E293B", border: "#94A3B8" },
  param: { bg: "#DBEAFE", color: "#1D4ED8", border: "#60A5FA" },
  "missing-param": { bg: "#FEE2E2", color: "#B91C1C", border: "#EF4444" },
  fn: { bg: "#EDE9FE", color: "#6D28D9", border: "#A78BFA" },
};

const DEFAULT_OPERATORS = ["+", "-", "*", "/", "(", ")", "^", "%", ","];
const DEFAULT_FUNCTIONS = [ "MAX", "MIN", "AVG", "STDDEV", "CV"];
const DEFAULT_TOKENS = [];
const EMPTY_LIST = [];
const FORMULA_VALIDATION_MESSAGES = {
  deletedParameter: "Formula is invalid. Remove the deleted parameter before submitting.",
  tableParameter: "Table-level parameters must be used inside a function.",
  incomplete: "Complete the formula before submitting.",
};
const FORMULA_EVALUATION_RESULTS = {
  error: "Error",
  missingValue: "Enter test value(s) to evaluate.",
};
const SECTION_STYLES = {
  border: "1px solid #CBD5E1",
  borderRadius: 1.5,
  backgroundColor: "#FFFFFF",
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
};
const ACTION_BUTTON_STYLES = {
  minWidth: 68,
  minHeight: 32,
  borderRadius: "6px",
  px: 1.75,
  py: 0.75,
  fontSize: 12,
  fontWeight: 400,
};

function getParameterDisplayLabel(parameter) {
  return parameter.name;
}

function getParameterTooltipContent(parameter) {
  const details = [
    ["Data Type", parameter.dataTypeLabel],
    ["Phase", parameter.phaseName],
    ["Table Level", parameter.tableLevelName],
    ["Unit of Measure", parameter.unitOfMeasure],
    ["Class", parameter.parameterClass],
    ["Description", parameter.description],
  ].filter(([, value]) => value !== undefined && value !== null && String(value).trim());

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, maxWidth: 280, py: 0.25 }}>
      {details.map(([label, value]) => (
        <Typography key={label} component="div" sx={{ color: "#FFFFFF", fontSize: 11, lineHeight: 1.4 }}>
          <Box component="span" sx={{ fontWeight: 600 }}>{label}:</Box>{" "}
          {String(value)}
        </Typography>
      ))}
    </Box>
  );
}

function ParameterInfoTooltip({ parameter }) {
  return (
    <Tooltip
      arrow
      placement="right"
      title={getParameterTooltipContent(parameter)}
    >
      <InfoOutlined
        tabIndex={0}
        aria-label={`View details for ${parameter.name}`}
        onClick={(event) => event.stopPropagation()}
        sx={{ ml: 1, flexShrink: 0, fontSize: 16, color: "#64748B", cursor: "help" }}
      />
    </Tooltip>
  );
}

function buildFormulaStr(tokens, useParameterIds = false) {
  let out = "";
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    const prev = tokens[i - 1];
    const next = tokens[i + 1];
    const noSpaceBefore =
      i === 0 ||
      t.label === ")" ||
      t.label === "(" ||
      (prev && (prev.label === "(" || prev.type === "fn" || t.type === "paren" && prev && prev.type === "fn"));
    const noSpaceAfter =
      !next ||
      next.label === ")" ||
      next.label === "(";
    if (!noSpaceBefore) out += " ";
    out += useParameterIds && t.type === "param" ? t.value : t.label;
  }
  return out;
}

function parseFormulaExpression(expression, formulaVariables, availableParameters) {
  if (!expression) return [];

  const expressionParameterIds = String(expression).match(/PARM-\d+/gi) || [];
  const savedVariables = Array.isArray(formulaVariables)
    ? formulaVariables
    : String(formulaVariables || "").match(/PARM-\d+/gi) || [];
  const variables = [...new Set([...savedVariables, ...expressionParameterIds])]
    .map(String)
    .sort((a, b) => b.length - a.length);
  const escapedVariables = variables.map((value) =>
    String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  );
  const tokenPattern = new RegExp(
    `${escapedVariables.length ? `${escapedVariables.join("|")}|` : ""}[A-Za-z_][A-Za-z0-9_]*|(?:\\d+\\.?\\d*|\\.\\d+)|[()+\\-*/^%,]`,
    "g"
  );

  return (String(expression).match(tokenPattern) || []).map((value) => {
    if (variables.includes(value)) {
      const parameter = availableParameters.find(
        (item) => String(item.id).toLowerCase() === value.toLowerCase()
      );
      return parameter
        ? { label: parameter.name, value, type: "param", parameterLevel: parameter.parameterLevel }
        : { label: `Deleted parameter (${value})`, value, type: "missing-param" };
    }
    if (value === "(" || value === ")") return { label: value, value, type: "paren" };
    if (DEFAULT_OPERATORS.includes(value)) return { label: value, value, type: "operator" };
    if (!Number.isNaN(Number(value))) return { label: value, value, type: "number" };
    return { label: value, value, type: "fn" };
  });
}

function hasTableParameterOutsideFunction(tokens) {
  const functionScopeStack = [];
  let functionPending = false;

  for (const token of tokens) {
    if (token.type === "fn") {
      functionPending = true;
      continue;
    }

    if (token.label === "(") {
      functionScopeStack.push(functionPending || functionScopeStack.includes(true));
      functionPending = false;
      continue;
    }

    if (token.label === ")") {
      functionScopeStack.pop();
      functionPending = false;
      continue;
    }

    if (
      token.type === "param" &&
      token.parameterLevel === "table" &&
      !functionScopeStack.includes(true)
    ) {
      return true;
    }

    functionPending = false;
  }

  return false;
}

function validateFormulaTokens(tokens) {
  if (
    !tokens.length ||
    tokens.some((token) => token.type === "missing-param") ||
    hasTableParameterOutsideFunction(tokens)
  ) return false;

  let depth = 0;
  const isOperand = (token) => token?.type === "number" || token?.type === "param";

  for (let index = 0; index < tokens.length; index += 1) {
    const token = tokens[index];
    const previous = tokens[index - 1];
    const next = tokens[index + 1];

    if (isOperand(token)) {
      if (isOperand(previous) || previous?.label === ")") return false;
      continue;
    }

    if (token.type === "fn") {
      if (isOperand(previous) || previous?.label === ")" || next?.label !== "(") return false;
      continue;
    }

    if (token.label === "(") {
      depth += 1;
      if (isOperand(previous) || previous?.label === ")") return false;
      if (!next || next.label === ")" || next.label === ",") return false;
      continue;
    }

    if (token.label === ")") {
      depth -= 1;
      if (depth < 0 || !previous || previous.type === "operator" || previous.label === "(" || previous.label === ",") return false;
      if (isOperand(next) || next?.type === "fn" || next?.label === "(") return false;
      continue;
    }

    if (token.label === ",") {
      if (!previous || (!isOperand(previous) && previous.label !== ")")) return false;
      if (!next || (!isOperand(next) && next.type !== "fn" && next.label !== "(")) return false;
      continue;
    }

    if (token.type === "operator") {
      if (!previous || (!isOperand(previous) && previous.label !== ")")) return false;
      if (!next || (!isOperand(next) && next.type !== "fn" && next.label !== "(")) return false;
    }
  }

  const lastToken = tokens[tokens.length - 1];
  return depth === 0 && (isOperand(lastToken) || lastToken.label === ")");
}

function getFunctionParameterNames(tokens) {
  const functionParameters = new Set();
  const functionScopeStack = [];
  let functionPending = false;

  tokens.forEach((token) => {
    if (token.type === "fn") {
      functionPending = true;
      return;
    }

    if (token.label === "(") {
      functionScopeStack.push(functionPending || functionScopeStack.includes(true));
      functionPending = false;
      return;
    }

    if (token.label === ")") {
      functionScopeStack.pop();
      functionPending = false;
      return;
    }

    if (token.type === "param" && functionScopeStack.includes(true)) {
      functionParameters.add(token.value);
    }
    functionPending = false;
  });

  return functionParameters;
}

function getTestInputConfig(dataType, allowsMultipleValues) {
  const normalizedType = String(dataType || "numeric").toLowerCase();

  if (normalizedType === "text" || normalizedType === "string") {
    return { type: "text", inputMode: "text", placeholder: "Enter text" };
  }
  if (normalizedType === "date" || normalizedType === "datetime") {
    return { type: "date", inputMode: undefined, placeholder: undefined };
  }
  return allowsMultipleValues
    ? { type: "text", inputMode: "decimal", placeholder: "100, 105" }
    : { type: "number", inputMode: "decimal", placeholder: "100" };
}

function normalizeParameterDataType(dataType) {
  const normalizedType = String(dataType || "numeric").trim().toLowerCase();

  if (["number", "numeric", "integer", "float", "decimal"].includes(normalizedType)) {
    return { value: "numeric", label: "Numeric" };
  }
  if (["text", "string"].includes(normalizedType)) {
    return { value: "text", label: "Text" };
  }
  if (["date", "datetime"].includes(normalizedType)) {
    return { value: "date", label: "Date" };
  }
  return {
    value: normalizedType,
    label: normalizedType.charAt(0).toUpperCase() + normalizedType.slice(1),
  };
}

function sanitizeNumericList(value) {
  return value.replace(/[^0-9,.-]/g, "");
}

function _toFiniteNumbers(args) {
  return args.map(Number).filter((value) => Number.isFinite(value));
}

function _AVG(...args) {
  const numbers = _toFiniteNumbers(args);
  return numbers.length ? numbers.reduce((s, v) => s + v, 0) / numbers.length : NaN;
}
function _STDDEV(...args) {
  const numbers = _toFiniteNumbers(args);
  if (!numbers.length) return NaN;
  const mean = _AVG(...numbers);
  const variance = numbers.reduce((sum, value) => sum + ((value - mean) ** 2), 0) / numbers.length;
  return Math.sqrt(variance);
}
function _CV(...args) {
  const mean = _AVG(...args);
  return mean === 0 ? NaN : (_STDDEV(...args) / mean) * 100;
}
function _IF(cond, a, b) { return cond ? a : b; }

function evalFormula(tokens, testValues) {
  try {
    const functionParameters = getFunctionParameterNames(tokens);
    let expr = buildFormulaStr(tokens.map(t => {
      if (t.type === "param") {
        const rawValue = String(testValues[t.value] ?? "");
        const values = (functionParameters.has(t.value) ? rawValue.split(",") : [rawValue])
          .map((value) => value.trim())
          .filter(Boolean)
          .map(Number);
        const numericValue = values.length > 0 && values.every(Number.isFinite)
          ? values.join(",")
          : "NaN";
        return { ...t, label: numericValue, value: numericValue };
      }
      return t;
    }), true);
    expr = expr.replace(/\^/g, "**");
    // eslint-disable-next-line no-new-func
    const result = new Function(
      "ABS", "ROUND", "MAX", "MIN", "AVG", "STDDEV", "CV", "IF", "SQRT", "LOG",
      `"use strict"; return (${expr})`
    )(
      Math.abs, Math.round, Math.max, Math.min,
      _AVG, _STDDEV, _CV, _IF, Math.sqrt, Math.log
    );
    if (typeof result === "number" && isFinite(result)) return result.toFixed(2);
    return FORMULA_EVALUATION_RESULTS.error;
  } catch {
    return FORMULA_EVALUATION_RESULTS.error;
  }
}

export default function FormulaBuilder({
  open,
  handleClose,
  isEdit = false,
  onSubmit,
  sourceParameters = EMPTY_LIST,
  sourceParametersLoading = false,
  sourceParametersError = "",
  initialFormulaExpression = "",
  initialFormulaVariables = EMPTY_LIST,
  parameterName = "",
}) {
  const [tokens, setTokens] = useState(DEFAULT_TOKENS);
  const [history, setHistory] = useState([DEFAULT_TOKENS]);
  const [histIdx, setHistIdx] = useState(0);
  const [parameters, setParameters] = useState([]);
  const [paramSearch, setParamSearch] = useState("");
  const [newParamName, setNewParamName] = useState("");
  const [showAddParam, setShowAddParam] = useState(false);
  const [numberInput, setNumberInput] = useState("");
  const [testValues, setTestValues] = useState({});
  const [evalResult, setEvalResult] = useState("");
  const [paramMandatory, setParamMandatory] = useState("Yes");
  const [paramType, setParamType] = useState("Derived");
  const [uom, setUom] = useState("");
  const [classVal, setClassVal] = useState("");
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");
  const [copied, setCopied] = useState(false);
  const [cursorIndex, setCursorIndex] = useState(0);
  const [submitErrorOpen, setSubmitErrorOpen] = useState(false);
  const formulaEditorRef = useRef(null);

  const availableParameters = useMemo(() => sourceParameters.map((parameter) => {
    const normalizedDataType = normalizeParameterDataType(parameter?.data_type ?? parameter?.type);
    return {
      id: parameter?.parameter_id ?? parameter?.parameter_code ?? parameter?.id ?? parameter?.parameter_name ?? parameter?.name ?? String(parameter),
      name: parameter?.parameter_name ?? parameter?.name ?? parameter?.label ?? String(parameter),
      dataType: normalizedDataType.value,
      dataTypeLabel: normalizedDataType.label,
      parameterLevel: String(parameter?.parameter_level || "").toLowerCase(),
      phaseName: parameter?.phase_name ?? parameter?.phase ?? "",
      tableLevelName: parameter?.table_level_name ?? parameter?.table_level ?? parameter?.table_name ?? "",
      unitOfMeasure: parameter?.unit_of_measure ?? parameter?.uom ?? "",
      parameterClass: parameter?.parameter_class ?? parameter?.class ?? "",
      description: parameter?.description ?? "",
    };
  }).filter((parameter) => parameter.name), [sourceParameters]);

  useEffect(() => {
    setParameters((current) => current.filter((id) =>
      availableParameters.some((parameter) => String(parameter.id) === String(id))
    ));
  }, [availableParameters]);

  useEffect(() => {
    if (sourceParametersLoading) return;

    const restoredTokens = parseFormulaExpression(
      initialFormulaExpression,
      initialFormulaVariables,
      availableParameters
    );
    const restoredParameterIds = [...new Set(
      restoredTokens.filter((token) => token.type === "param").map((token) => token.value)
    )];

    setTokens(restoredTokens);
    setHistory([restoredTokens]);
    setHistIdx(0);
    setCursorIndex(restoredTokens.length);
    setParameters(restoredParameterIds);
    setTestValues(Object.fromEntries(restoredParameterIds.map((id) => [id, ""])));
    setEvalResult("");
  }, [initialFormulaExpression, initialFormulaVariables, availableParameters, sourceParametersLoading]);

  const pushHistory = useCallback((newTokens, nextCursorIndex = newTokens.length) => {
    const trimmed = history.slice(0, histIdx + 1);
    setHistory([...trimmed, newTokens]);
    setHistIdx(trimmed.length);
    setTokens(newTokens);
    setCursorIndex(Math.max(0, Math.min(nextCursorIndex, newTokens.length)));
  }, [history, histIdx]);

  const undo = () => {
    if (histIdx > 0) {
      const previousTokens = history[histIdx - 1];
      setHistIdx(h => h - 1);
      setTokens(previousTokens);
      setCursorIndex(previousTokens.length);
    }
  };
  const redo = () => {
    if (histIdx < history.length - 1) {
      const nextTokens = history[histIdx + 1];
      setHistIdx(h => h + 1);
      setTokens(nextTokens);
      setCursorIndex(nextTokens.length);
    }
  };

  const insertTokens = (newTokens) => {
    const updatedTokens = [
      ...tokens.slice(0, cursorIndex),
      ...newTokens,
      ...tokens.slice(cursorIndex),
    ];
    pushHistory(updatedTokens, cursorIndex + newTokens.length);
  };

  const addToken = (label, type, value = label, metadata = {}) => (
    insertTokens([{ label, type, value, ...metadata }])
  );
  const removeToken = (idx) => {
    const updatedTokens = tokens.filter((_, i) => i !== idx);
    const nextCursorIndex = idx < cursorIndex ? cursorIndex - 1 : cursorIndex;
    pushHistory(updatedTokens, nextCursorIndex);
  };
  const clearTokens = () => pushHistory([], 0);

  const handleFormulaKeyDown = (event) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      setCursorIndex((current) => Math.max(0, current - 1));
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      setCursorIndex((current) => Math.min(tokens.length, current + 1));
    } else if (event.key === "Backspace" && cursorIndex > 0) {
      event.preventDefault();
      removeToken(cursorIndex - 1);
    } else if (event.key === "Delete" && cursorIndex < tokens.length) {
      event.preventDefault();
      removeToken(cursorIndex);
    }
  };

  const addNumberToken = () => {
    if (numberInput.trim() !== "" && !isNaN(numberInput)) {
      addToken(numberInput.trim(), "number");
      setNumberInput("");
    }
  };

  const addParam = () => {
    const name = newParamName.trim();
    if (name && !parameters.includes(name)) {
      setParameters(p => [...p, name]);
      setTestValues(v => ({ ...v, [name]: "" }));
    }
    setNewParamName("");
    setShowAddParam(false);
  };

  const removeParam = (parameterId) => {
    setParameters(p => p.filter(id => String(id) !== String(parameterId)));
    setTestValues(v => { const c = { ...v }; delete c[parameterId]; return c; });
    pushHistory(tokens.filter(t => String(t.value) !== String(parameterId)));
  };

  const formulaStr = buildFormulaStr(tokens).replace(/\s+/g, "");
  const formulaExpression = buildFormulaStr(tokens, true).replace(/\s+/g, "");
  const charLen = formulaExpression.length;
  const testParameters = useMemo(() => Array.from(
    new Map(
      tokens
        .filter((token) => token.type === "param")
        .map((token) => [String(token.value), { id: token.value, name: token.label }])
    ).values()
  ), [tokens]);
  const functionParameterNames = useMemo(() => getFunctionParameterNames(tokens), [tokens]);
  const hasMissingParameters = tokens.some((token) => token.type === "missing-param");
  const hasInvalidTableParameter = hasTableParameterOutsideFunction(tokens);
  const validationMessage = hasMissingParameters
    ? FORMULA_VALIDATION_MESSAGES.deletedParameter
    : hasInvalidTableParameter
      ? FORMULA_VALIDATION_MESSAGES.tableParameter
      : FORMULA_VALIDATION_MESSAGES.incomplete;

  useEffect(() => {
    setTestValues((currentValues) => Object.fromEntries(
      testParameters.map((parameter) => [
        parameter.id,
        currentValues[parameter.id] ?? "",
      ])
    ));
  }, [testParameters]);

  const isValid = validateFormulaTokens(tokens);

  useEffect(() => {
    if (isValid || !open) setSubmitErrorOpen(false);
  }, [isValid, open]);

  const handleEvaluate = () => {
    const hasEmptyTestValue = testParameters.some(
      (parameter) => String(testValues[parameter.id] ?? "").trim() === "",
    );
    setEvalResult(
      hasEmptyTestValue
        ? FORMULA_EVALUATION_RESULTS.missingValue
        : evalFormula(tokens, testValues),
    );
  };

  const copyFormula = () => {
    navigator.clipboard.writeText(formulaStr).catch(() => { });
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleSubmit = () => {
    if (!isValid) {
      setSubmitErrorOpen(true);
      return;
    }
    const formulaVariables = [...new Set(
      tokens.filter((token) => token.type === "param").map((token) => token.value)
    )];
    onSubmit?.({ formulaExpression, formulaVariables });
  };

  const filteredParams = parameters
    .map((id) => availableParameters.find((parameter) => String(parameter.id) === String(id)))
    .filter(Boolean)
    .filter((parameter) => (
      [parameter.name, parameter.phaseName, parameter.tableLevelName]
        .some((value) => String(value).toLowerCase().includes(paramSearch.toLowerCase()))
    ));


  return (
    <Dialog
      open={open}
      onClose={handleClose}
      hideBackdrop
      disableEnforceFocus
      keepMounted={false}
      PaperProps={{
        sx: {
          position: "fixed",
          bottom: 0,
          left: 55,
          m: 0,
          maxHeight: "calc(100% - 50px)",
          height: "100%",
          boxShadow: "0 12px 32px rgba(15, 23, 42, 0.18)",
          elevation: 0,
          border: "1px solid #94A3B8",
          transition: "width 0.3s ease",
          width: "calc(100% - 55px)",
          maxWidth: "calc(100% - 555px)",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#94A3B8",
          },
          "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#475569",
          },
          "& .MuiInputBase-input": {
            color: "#1E293B",
            fontWeight: 400,
          },
        },
      }}
    >
      <CustomizedSnackbars
        state={submitErrorOpen}
        severity="error"
        text={validationMessage}
        handleClose={() => setSubmitErrorOpen(false)}
      />
      <Box
        sx={{
          display: "flex",
          width: "100%",
          height: "100%",
          bgcolor: "#fff",

          // 🔥 IMPORTANT
          overflow: "visible"
        }}
      >

        {/* ===== LEFT PANEL ===== */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }}>

          {/* Header */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 1.5, borderBottom: "1px solid #CBD5E1", bgcolor: "#F8FAFC", flexShrink: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 3, minWidth: 0, flex: 1 }}>
              <Box sx={{ flexShrink: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: 15, color: "#111827" }}>Formula Builder</Typography>
                <Typography sx={{ fontSize: 12, color: "#475569" }}>Create and manage derived parameter formulas</Typography>
              </Box>
              {parameterName && (
                <Box sx={{ minWidth: 0, borderLeft: "1px solid #CBD5E1", pl: 3 }}>
                  <Typography sx={{ color: "#64748B", fontSize: 10, lineHeight: 1.2 }}>
                    PARAMETER
                  </Typography>
                  <Typography
                    title={parameterName}
                    sx={{
                      color: "#1D4ED8",
                      fontSize: 17,
                      fontWeight: 600,
                      lineHeight: 1.35,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {parameterName}
                  </Typography>
                </Box>
              )}
            </Box>
            <IconButton onClick={handleClose} size="small" sx={{ color: "#6B7280" }}><Close fontSize="small" /></IconButton>
          </Box>

          <Box sx={{ flex: 1, overflowY: "auto", p: 2.5, display: "flex", flexDirection: "column", gap: 2, bgcolor: "#F8FAFC" }}>

            {/* Formula Expression */}
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#111827" }}>Formula Expression</Typography>
              </Box>

              {/* Toolbar */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", border: "1px solid #CBD5E1", borderBottom: "none", borderRadius: "6px 6px 0 0", px: 1.5, py: 0.75, bgcolor: "#F1F5F9" }}>
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  {[
                    { icon: <Undo sx={{ fontSize: 13 }} />, label: "Undo", action: undo, disabled: histIdx === 0 },
                    { icon: <Redo sx={{ fontSize: 13 }} />, label: "Redo", action: redo, disabled: histIdx >= history.length - 1 },
                    { icon: <ContentCopy sx={{ fontSize: 13 }} />, label: copied ? "Copied!" : "Copy", action: copyFormula },
                    { icon: <Close sx={{ fontSize: 13 }} />, label: "Clear", action: clearTokens },
                  ].map(({ icon, label, action, disabled }) => (
                    <Button key={label} startIcon={icon} size="small" disabled={disabled}
                      onClick={action}
                      sx={{ textTransform: "none", fontSize: 12, color: "#374151", minWidth: 0, px: 1, py: 0.4, "&:hover": { bgcolor: "#F3F4F6" } }}>
                      {label}
                    </Button>
                  ))}
                </Box>
              </Box>

              {/* Token area */}
              <Box
                ref={formulaEditorRef}
                role="textbox"
                aria-label="Formula expression editor"
                tabIndex={0}
                onClick={(event) => {
                  if (event.target === event.currentTarget) setCursorIndex(tokens.length);
                }}
                onKeyDown={handleFormulaKeyDown}
                sx={{ border: "1px solid #CBD5E1", borderRadius: "0 0 6px 6px", p: 1.5, minHeight: 80, display: "flex", flexWrap: "wrap", gap: 0.75, alignContent: "flex-start", bgcolor: "#fff", cursor: "text", "&:focus": { outline: "2px solid #93C5FD", outlineOffset: "-2px" } }}
              >
                {tokens.length === 0 && (
                  <Box aria-hidden="true" className="formula-builder-caret" />
                )}
                {tokens.length === 0 && (
                  <Typography sx={{ fontSize: 12, color: "#64748B", fontStyle: "italic" }}>
                    Add operators, functions, or parameters below to build your formula…
                  </Typography>
                )}
                {tokens.map((t, i) => (
                  <Box key={`${t.type}-${t.value}-${i}`} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    {cursorIndex === i && (
                      <Box aria-hidden="true" className="formula-builder-caret" />
                    )}
                    <Tooltip
                      arrow
                      placement="top"
                      disableHoverListener={t.type !== "param"}
                      title={t.type === "param"
                        ? getParameterTooltipContent(
                          availableParameters.find(
                            (parameter) => String(parameter.id) === String(t.value)
                          )
                        )
                        : ""}
                    >
                      <Chip label={t.label} size="small"
                        onClick={(event) => {
                          const bounds = event.currentTarget.getBoundingClientRect();
                          setCursorIndex(event.clientX < bounds.left + bounds.width / 2 ? i : i + 1);
                          formulaEditorRef.current?.focus();
                        }}
                        onDelete={() => removeToken(i)}
                        deleteIcon={<Close sx={{ fontSize: 11, color: TOKEN_COLORS[t.type].color + " !important" }} />}
                        sx={{
                          bgcolor: TOKEN_COLORS[t.type].bg, color: TOKEN_COLORS[t.type].color,
                          border: `1px solid ${TOKEN_COLORS[t.type].border}`,
                          fontSize: 12, fontWeight: 400,
                          height: 26, borderRadius: 1, cursor: "text",
                          "& .MuiChip-label": { px: 1 },
                          "& .MuiChip-deleteIcon": { mr: 0.25 },
                        }}
                      />
                    </Tooltip>
                  </Box>
                ))}
                {tokens.length > 0 && cursorIndex === tokens.length && (
                  <Box aria-hidden="true" className="formula-builder-caret" />
                )}
              </Box>

              {/* Number input row */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                <TextField placeholder="Enter a number…" size="small" value={numberInput}
                  onChange={e => setNumberInput(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && addNumberToken()}
                  sx={{ width: 160, "& .MuiOutlinedInput-root": { fontSize: 12, borderRadius: 1 } }} />
                <Button variant="outlined" size="small" onClick={addNumberToken}
                  sx={{ textTransform: "none", fontSize: 12, color: "#374151", borderColor: "#D1D5DB", borderRadius: 1 }}>
                  Add Number
                </Button>
                <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.5 }}>
                  {isValid
                    ? <><CheckCircle sx={{ fontSize: 14, color: "#16A34A" }} /><Typography sx={{ fontSize: 12, color: "#16A34A" }}>Formula is valid</Typography></>
                    : <Typography sx={{ fontSize: 12, color: "#EF4444" }}>
                      {validationMessage}
                    </Typography>
                  }
                  <Typography sx={{ fontSize: 12, color: "#9CA3AF", ml: 1 }}>Length: {charLen} characters</Typography>
                </Box>
              </Box>
            </Box>

            {/* Bottom 3-col */}
            <Box sx={{ display: "flex", gap: 2, flex: 1 }}>

              {/* Parameters */}
              <Box sx={{ ...SECTION_STYLES, flex: 1, p: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#111827" }}>Parameters</Typography>
                <TextField placeholder="Search parameters…" size="small" value={paramSearch}
                  onChange={e => setParamSearch(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 15, color: "#9CA3AF" }} /></InputAdornment>, sx: { fontSize: 12 } }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }} />

                {showAddParam ? (
                  <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <Select
                        value=""
                        displayEmpty
                        onChange={(e) => {
                          const value = e.target.value;
                          if (!value) return;
                          const selectedParameter = availableParameters.find(
                            (parameter) => String(parameter.id) === String(value)
                          );

                          setParameters((currentParameters) => (
                            currentParameters.some((id) => String(id) === String(value))
                              ? currentParameters
                              : [...currentParameters, value]
                          ));

                          setTestValues((v) => ({
                            ...v,
                            [selectedParameter?.id || value]: v[selectedParameter?.id || value] ?? "",
                          }));

                          setShowAddParam(false);
                        }}
                        sx={{
                          fontSize: 12,
                          borderRadius: 1,
                        }}

                        // ✅ IMPORTANT FIX: force menu to render outside stacking context
                        MenuProps={{
                          disablePortal: false,
                          container: document.body,

                          PaperProps: {
                            sx: {
                              maxHeight: 260,
                              zIndex: 99999, // 🔥 highest priority
                            },
                          },

                          // ✅ extra safety for Popover layer
                          PopoverClasses: {
                            root: "mui-select-popover",
                          },
                        }}
                        renderValue={() => "Select parameter…"}
                      >
                        {sourceParametersLoading && (
                          <MenuItem disabled sx={{ fontSize: 12, gap: 1 }}>
                            <CircularProgress size={14} /> Loading parameters...
                          </MenuItem>
                        )}
                        {!sourceParametersLoading && sourceParametersError && (
                          <MenuItem disabled sx={{ fontSize: 12, color: "error.main" }}>
                            {sourceParametersError}
                          </MenuItem>
                        )}
                        {!sourceParametersLoading && !sourceParametersError && availableParameters.length === 0 && (
                          <MenuItem disabled sx={{ fontSize: 12 }}>No parameters available</MenuItem>
                        )}
                        {availableParameters.map((parameter) => (
                          <MenuItem
                            key={parameter.id}
                            value={parameter.id}
                            disabled={parameters.some((id) => String(id) === String(parameter.id))}
                            sx={{ display: "flex", justifyContent: "space-between", gap: 1, fontSize: 12 }}
                          >
                            <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                              {getParameterDisplayLabel(parameter)}
                            </Box>
                            <ParameterInfoTooltip parameter={parameter} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <IconButton
                      size="small"
                      onClick={() => setShowAddParam(false)}
                    >
                      <Close sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                ) : (
                  <Button
                    startIcon={<Add sx={{ fontSize: 14 }} />}
                    size="small"
                    onClick={() => setShowAddParam(true)}
                    sx={{
                      textTransform: "none",
                      fontSize: 12,
                      color: "#1A56DB",
                      justifyContent: "flex-start",
                      px: 0.5,
                    }}
                  >
                    Add Parameter
                  </Button>
                )}

                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, overflowY: "auto", maxHeight: 260 }}>
                  {filteredParams.map((parameter) => (
                    <Box key={parameter.id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 0.5, py: 0.4, borderRadius: 1, "&:hover": { bgcolor: "#F0F4FF", cursor: "pointer" } }}
                      onClick={() => {
                        addToken(parameter.name, "param", parameter.id, {
                          parameterLevel: parameter?.parameterLevel,
                        });
                      }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Tag sx={{ fontSize: 13, color: "#1A56DB" }} />
                        <Box>
                          <Typography sx={{ fontSize: 12, color: "#111827", lineHeight: 1.3 }}>
                            {getParameterDisplayLabel(parameter)}
                          </Typography>
                          <Typography sx={{ fontSize: 10, color: "#64748B" }}>
                            {parameter.dataTypeLabel || "Numeric"}
                            {parameter.parameterLevel === "table"
                              ? " • Table level"
                              : ""}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                        <ParameterInfoTooltip parameter={parameter} />
                        <IconButton size="small" onClick={e => { e.stopPropagation(); removeParam(parameter.id); }}
                          sx={{ opacity: 0.4, "&:hover": { opacity: 1, color: "#EF4444" } }}>
                          <DeleteOutline sx={{ fontSize: 13 }} />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Operators + Functions */}
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box sx={{ ...SECTION_STYLES, p: 1.5 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#111827", mb: 1 }}>Operators</Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                    {DEFAULT_OPERATORS.map(op => (
                      <Button key={op} variant="outlined" size="small" onClick={() => addToken(op, op === "(" || op === ")" ? "paren" : "operator")}
                        sx={{ minWidth: 40, fontSize: 13, color: "#374151", borderColor: "#D1D5DB", borderRadius: 1, px: 1, py: 0.5, fontWeight: 400, "&:hover": { bgcolor: "#F0F4FF", borderColor: "#1A56DB", color: "#1A56DB" } }}>
                        {op}
                      </Button>
                    ))}
                  </Box>
                </Box>

                <Box sx={{ ...SECTION_STYLES, p: 1.5 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#111827", mb: 1 }}>Functions</Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                    {DEFAULT_FUNCTIONS.map(fn => (
                      <Button key={fn} variant="outlined" size="small"
                        onClick={() => insertTokens([{ label: fn, type: "fn" }, { label: "(", type: "paren" }])}
                        sx={{ minWidth: 50, fontSize: 12, color: "#7C3AED", borderColor: "#C4B5FD", borderRadius: 1, px: 1, py: 0.5, "&:hover": { bgcolor: "#F5F3FF", borderColor: "#7C3AED" } }}>
                        {fn}
                      </Button>
                    ))}
                  </Box>
                </Box>
              </Box>

              {/* Formula Preview + Test */}
              <Box sx={{ flex: 1.4, display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box sx={{ ...SECTION_STYLES, p: 1.5 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#111827", mb: 1 }}>Formula Preview</Typography>
                  <Box sx={{ bgcolor: "#F8FAFC", border: "1px solid #CBD5E1", borderRadius: 1, p: 1, position: "relative", minHeight: 60 }}>
                    <Typography sx={{ fontFamily: "monospace", fontSize: 11.5, color: "#374151", lineHeight: 1.7, pr: 3, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                      {formulaStr || <span style={{ color: "#9CA3AF", fontStyle: "italic" }}>No formula yet…</span>}
                    </Typography>
                    <IconButton size="small" onClick={copyFormula} sx={{ position: "absolute", top: 4, right: 4, color: "#9CA3AF" }}>
                      <ContentCopy sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                </Box>

                <Box sx={{ ...SECTION_STYLES, p: 1.5 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#111827", mb: 1 }}>Test Formula</Typography>

                  {/* Header row */}
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 80px 70px", gap: 0.75, mb: 0.5 }}>
                    <Typography sx={{ fontSize: 11, color: "#6B7280", fontWeight: 600 }}>Parameter</Typography>
                    <Typography sx={{ fontSize: 11, color: "#6B7280", fontWeight: 600 }}>Test Value(s)</Typography>
                    <Typography sx={{ fontSize: 11, color: "#6B7280", fontWeight: 600 }}>Type</Typography>
                  </Box>

                  <Box sx={{ maxHeight: 130, overflowY: "auto", display: "flex", flexDirection: "column", gap: 0.5, mb: 1 }}>
                    {testParameters.map((testParameter) => {
                      const parameterDataType = availableParameters.find(
                        (parameter) => String(parameter.id) === String(testParameter.id)
                      )?.dataType || "numeric";
                      const allowsMultipleValues = functionParameterNames.has(testParameter.id);
                      const inputConfig = getTestInputConfig(parameterDataType, allowsMultipleValues);
                      const isMissingTestValue =
                        evalResult === FORMULA_EVALUATION_RESULTS.missingValue &&
                        String(testValues[testParameter.id] ?? "").trim() === "";

                      return (
                      <Box key={testParameter.id} sx={{ display: "grid", gridTemplateColumns: "1fr 80px 70px", gap: 0.75, alignItems: "center" }}>
                        <Typography sx={{ fontSize: 11, color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {getParameterDisplayLabel(
                            availableParameters.find(
                              (parameter) => String(parameter.id) === String(testParameter.id)
                            ) || { name: testParameter.name }
                          )}
                        </Typography>
                        <TextField
                          value={testValues[testParameter.id] ?? ""}
                          size="small"
                          type={inputConfig.type}
                          placeholder={inputConfig.placeholder}
                          inputProps={{
                            "aria-label": `Test values for ${testParameter.name}`,
                            inputMode: inputConfig.inputMode,
                          }}
                          onChange={(event) => {
                            const value = allowsMultipleValues && inputConfig.inputMode === "decimal"
                              ? sanitizeNumericList(event.target.value)
                              : event.target.value;
                            setTestValues((currentValues) => ({
                              ...currentValues,
                              [testParameter.id]: value,
                            }));
                            setEvalResult("");
                          }}
                          sx={{
                            "& input": { fontSize: 11, py: 0.4, px: 0.75 },
                            ...(isMissingTestValue && {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#EF4444",
                                borderWidth: 2,
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#DC2626",
                              },
                              "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#DC2626",
                              },
                            }),
                          }} />
                        <Typography sx={{ fontSize: 11, color: "#6B7280" }}>
                          {availableParameters.find(
                            (parameter) => String(parameter.id) === String(testParameter.id)
                          )?.dataTypeLabel || "Numeric"}
                        </Typography>
                      </Box>
                    )})}
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Button startIcon={<PlayArrow sx={{ fontSize: 14 }} />} variant="outlined" size="small"
                      onClick={handleEvaluate}
                      sx={{ textTransform: "none", fontSize: 12, color: "#374151", borderColor: "#D1D5DB", borderRadius: 1 }}>
                      Evaluate
                    </Button>
                    {evalResult && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Typography sx={{
                          fontSize: evalResult === FORMULA_EVALUATION_RESULTS.missingValue ? 14 : 24,
                          fontWeight: 700,
                          color: evalResult === FORMULA_EVALUATION_RESULTS.error
                            ? "#EF4444"
                            : evalResult === FORMULA_EVALUATION_RESULTS.missingValue
                              ? "#B54708"
                              : "#16A34A",
                        }}>
                          {evalResult}
                        </Typography>
                        {![
                          FORMULA_EVALUATION_RESULTS.error,
                          FORMULA_EVALUATION_RESULTS.missingValue,
                        ].includes(evalResult) && (
                          <CheckCircle sx={{ fontSize: 14, color: "#16A34A" }} />
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1, px: 2, py: 1.5, borderTop: "1px solid #CBD5E1", bgcolor: "#FFFFFF", width: "100%", justifyContent: "flex-end" }}>
            <Buttons colors="primary" size="small" text="Submit" onClick={handleSubmit} sx={ACTION_BUTTON_STYLES} />
            <Buttons colors="gray" size="small" text="Cancel" onClick={handleClose} sx={ACTION_BUTTON_STYLES} />
          </Box>
        </Box>


      </Box>
    </Dialog>
  );
}
